// src/utils/calculatePointsFromMatches.js
// Note: Transfer penalties and transfer-related logic are handled in calculateTeamPoints.js,
// not in this file. This file focuses solely on calculating per-player points from match events.

import { doc, setDoc, getDocs } from "firebase/firestore";
import { paths } from "../lib/paths";
import { getLeagueConfig } from "./leagueConfig";
import { INFRACTION_POINTS, LEAGUE_RULESETS, CANON_TO_LABEL } from "../utils/scoringRules";
import { logError } from "./logError";

// Utilities to normalize labels/aliases
const keyify = (s) =>
  String(s || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

const TYPE_ALIAS = {
  yellow: "yellowcard",
  yellowcard: "yellowcard",
  "own goal": "owngoal",
  owngoal: "owngoal",
  "penalty miss": "penaltymiss",
  penaltymiss: "penaltymiss",
  gkSaveBonus: "gksavebonus",
  "penalty save": "penaltysave",
  penaltysave: "penaltysave",
};

const toCanon = (label) => TYPE_ALIAS[keyify(label)] || keyify(label);



export async function calculatePointsFromMatches(rawLeagueID, rawGwID, matches) {
  try {
    const leagueID = String(rawLeagueID);
    const gwID = String(rawGwID);
    if (!leagueID || !gwID) return;

    const config = await getLeagueConfig(leagueID);
    let leagueType = config?.type || config?.leagueType || config?.label;

    // normalize to string
    leagueType = String(leagueType || "").trim();

      // fallback aliases
      const TYPE_NORMALIZATION = {
        "5": "5",
        "7": "7",
        "9": "9",
        "11": "11",
        "5_a_side": "5",
        "7_a_side": "7",
        "9_a_side": "9",
        "11_a_side": "11",
        "5-a-side": "5",
        "7-a-side": "7",
        "9-a-side": "9",
        "11-a-side": "11",
      };
    
      leagueType = TYPE_NORMALIZATION[leagueType.toLowerCase()] || leagueType;

    if (!leagueType) {
      console.warn(`No league type found for league ${leagueID}, aborting calculation.`, config);
      return;
    }

    const SCORES = LEAGUE_RULESETS[leagueType];
    if (!SCORES) {
      console.warn(`No scoring rules found for league type ${leagueType}, aborting calculation.`, config);
      return;
    }

    console.log("▶️ calculatePointsFromMatches", {
      leagueID, gwID, matchesCount: matches?.length || 0
    });

    // Build player lookups
    const playersRef = paths.leaguePlayers(leagueID);
    const playersSnap = await getDocs(playersRef);
    const posOf = new Map();     // id -> position
    const idByName = new Map();  // lower(name) -> id
    const nameById = new Map();
    playersSnap.forEach((d) => {
      const p = d.data() || {};
      posOf.set(d.id, p.position || "FWD");
      if (p.name) {
        idByName.set(String(p.name).toLowerCase(), d.id);
        nameById.set(d.id, p.name);
      }
    });

    const ensure = (store, pid) => {
      if (!store[pid]) store[pid] = { total: 0, breakdown: {} };
      return store[pid];
    };
    const add = (store, pid, key, val) => {
      const rec = ensure(store, pid);
      if (key === "isGK" && typeof val === "boolean") {
        rec.breakdown.isGK = true;
      } else if (typeof val === "number") {
        rec.breakdown[key] = (rec.breakdown[key] || 0) + val;
      }
    };
    const coerceId = (v) => {
      if (!v) return undefined;
      if (typeof v === "string") {
        if (posOf.has(v)) return v; // looks like an id
        const byName = idByName.get(v.toLowerCase());
        if (byName) return byName;
        return undefined;
      }
      if (typeof v === "object") {
        return coerceId(v.id || v.playerId || v.player || v.name);
      }
      return undefined;
    };

    const playerPoints = {};

    const dbg = (...args) => {
      try { console.log(...args); } catch (_) {}
    };

    for (const match of matches || []) {
      try {
        const {
          appearanceData = {},
          starters = {},
          goals = [],
          assists = {},
          cleanSheet = false,
          bestPlayers = {},
        } = match || {};

        // These are the two fields written by MatchInput
        // ✅ Appearances (+2) & GK flag
        for (const pid of Object.keys(appearanceData)) {
          const ap = appearanceData[pid] || {};
          if (ap.appeared) {
            add(playerPoints, pid, "appearance", SCORES.appearance);
            if (ap.isGK) add(playerPoints, pid, "isGK", true);
          }
        }

        // ✅ Starters (+1)
        for (const pid of Object.keys(starters)) {
          if (starters[pid]) add(playerPoints, pid, "starter", SCORES.starter);
        }

        // Track which players have already been credited assists
        const creditedAssists = new Set();

        // ✅ Goals (+ inline assists, if any)
        const goalEvents = Array.isArray(goals) ? goals : Object.values(goals || {});
        for (const g of goalEvents) {
          const scorerId =
            coerceId(g?.scorerId) ||
            coerceId(g?.scorer) ||
            coerceId(g?.playerId) ||
            coerceId(g?.player);
          if (scorerId) {
            const pos = posOf.get(scorerId) || "FWD";
            const pts = SCORES.goal[pos] || 0;
            if (pts) add(playerPoints, scorerId, "goals", pts);
            dbg("▶️ Goal scored", { scorerId, pos, pts });
          }
          const assistId =
            coerceId(g?.assistId) ||
            coerceId(g?.assist) ||
            coerceId(g?.assisterId) ||
            coerceId(g?.assister);
          if (assistId) {
            add(playerPoints, assistId, "assists", SCORES.assist);
            creditedAssists.add(assistId);
            dbg("▶️ Assist", { assistId, pts: SCORES.assist });
          }
        }

        // ✅ Always parse assists map if exists and has keys
        if (assists && Object.keys(assists).length) {
          for (const [pid, count] of Object.entries(assists)) {
            if (!pid) continue;
            const aid = coerceId(pid);
            if (!aid || creditedAssists.has(aid)) continue;
            if (typeof count === "number" && count > 0) {
              add(playerPoints, aid, "assists", count * SCORES.assist);
              dbg("▶️ Assist map", { assistId: aid, count, pts: count * SCORES.assist });
            }
          }
        }

        // ✅ Clean sheet (+ GK bonus)
        if (cleanSheet) {
          dbg("▶️ Clean sheet true, evaluating appeared players");
          for (const pid of Object.keys(appearanceData)) {
            const ap = appearanceData[pid] || {};
            if (!ap.appeared) continue;
            if (ap.isGK) {
              add(playerPoints, pid, "cleanSheetGK", 7);
              dbg("   ↳ CS award GK", { pid, pts: 7 });
            } else {
              const pos = posOf.get(pid) || "DEF";
              const cs = SCORES.cleanSheet[pos] || 0;
              if (cs) add(playerPoints, pid, "cleanSheet", cs);
              dbg("   ↳ CS award", { pid, pos, cs });
            }
          }
        }

        // ✅ Infractions
        const rawInfractions =
          match?.infractionAssignments ||
          match?.Infractions ||
          {};

        dbg("▶️ Raw infractionAssignments", rawInfractions);

        if (rawInfractions && Object.keys(rawInfractions).length) {
          for (const [rawType, val] of Object.entries(rawInfractions)) {
            const canon = toCanon(rawType);
            const delta = INFRACTION_POINTS[canon];
            dbg("  ↳ Checking infraction", { rawType, canon, delta, val });

            if (delta === undefined) {
              console.warn("⚠️ Unknown infraction type", rawType, canon);
              continue;
            }

            let vals = [];
            if (typeof val === "string") vals = [val];
            else if (Array.isArray(val)) vals = val;
            else if (typeof val === "object" && val !== null) vals = Object.values(val);

            for (const who of vals) {
              const pid = coerceId(who);
              if (pid) {
                add(playerPoints, pid, CANON_TO_LABEL[canon] || canon, delta);
                dbg("▶️ Infraction applied", {
                  type: CANON_TO_LABEL[canon] || canon,
                  pid,
                  delta,
                });
              }
            }
          }
        }

        const bonusGivenForRank = new Set();
        for (const [rank, raw] of Object.entries(bestPlayers || {})) {
          const pid = coerceId(raw);
          const key = String(rank);
          const b = SCORES.bonusByRank[key];
          if (pid && b && !bonusGivenForRank.has(key)) {
            add(playerPoints, pid, "bonus", b);
            bonusGivenForRank.add(key);
            dbg("▶️ Bonus", { rank: key, pid, pts: b });
          }
        }
      } catch (err) {
        await logError({
          leagueID,
          userID: "system",
          action: "calculatePointsFromMatches_match",
          payload: { matchIndex: matches.indexOf(match) },
          error: err
        });
        continue;
      }
    }

    // ✅ Recalculate totals from breakdown (only numeric values)
    for (const pid in playerPoints) {
      const rec = playerPoints[pid];
      const sum = Object.values(rec.breakdown || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
      rec.total = sum;
    }

    console.log("✅ playerPoints (base):", playerPoints);

    // 🔥 Save results to Firestore (overwrite fully to avoid stale totals)
    await Promise.all(
      Object.entries(playerPoints).map(([pid, rec]) => {
        // recalc total as sum of breakdown to ensure fresh
        const breakdown = rec.breakdown || {};
        const freshTotal = Object.values(breakdown).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
        const clean = {
          total: freshTotal,
          breakdown,
          name: nameById.get(pid) || null,
        };
        console.log("📝 Writing player points", pid, clean);
        return setDoc(
          doc(paths.gwPlayerPoints(leagueID, gwID), pid),
          clean,
          { merge: false }
        );
      })
    );

    console.log(`🎉 Saved ${Object.keys(playerPoints).length} players for ${leagueID}/${gwID}`);
    return playerPoints;
  } catch (err) {
    await logError({
      leagueID: rawLeagueID,
      userID: "system",
      action: "calculatePointsFromMatches_main",
      payload: { gwID: rawGwID, matchesCount: matches?.length || 0 },
      error: err
    });
    throw err;
  }
}